import React, { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'

import { HELP_LINKS } from 'app/helpLinks'
import { useAppContext } from 'app/useAppContext'
import { useObject } from 'data/hooks/objects'
import { LOOKUP_TYPES } from 'features/admin/fields/common'
import { getIsSymmetricRelationshipField, getIsSyntheticField } from 'utils/fieldUtils'

import { Banner, Box, Flex, Text } from 'v2/ui'

import { FormField } from 'ui/deprecated/forms/FormField'

import { DefaultValueWrapper } from './DefaultValueWrapper'

type DefaultValueConfigurationProps = {
    field: FieldDto
    object?: ObjectDto
}
export const FieldDefaultValueConfiguration: React.FC<DefaultValueConfigurationProps> = ({
    field,
    object,
}) => {
    const [showWarning, setShowWarning] = useState(false)
    const { selectedStack } = useAppContext()
    const linkTargetObjectId = useWatch({ name: 'link_target_object_id' })
    const defaultValue: FieldDto['default_value'] = useWatch({ name: 'default_value' })
    const isUserObject =
        object?._sid && object._sid === selectedStack?.options?.data_mapping?.user_object
    const { object: userObject } = useObject(selectedStack?.options?.data_mapping?.user_object)

    useEffect(() => {
        // if the user has selected a multi-link to use as default value for a single link, we want to show a warning
        // that only one of the value will be taken as the default
        const userObjFields = userObject?.fields ?? []
        let newShowWarning = false
        if (defaultValue && defaultValue.type === 'user' && defaultValue.value) {
            const selectedUserField = userObjFields.find(
                (field) => field.api_name === defaultValue.value
            )
            if (
                selectedUserField &&
                selectedUserField.type === 'multi_lookup' &&
                field.type === 'lookup'
            ) {
                newShowWarning = true
            }
        }

        if (newShowWarning !== showWarning) {
            setShowWarning(newShowWarning)
        }
    }, [defaultValue, field.type, showWarning, userObject?.fields])

    if (
        (LOOKUP_TYPES.includes(field.type) && !linkTargetObjectId) ||
        getIsSymmetricRelationshipField(field) ||
        getIsSyntheticField(field) ||
        isUserObject
    ) {
        // - For relationship fields, default value config for relationship fields is only shown once
        //   the link target object is selected.
        // - This config is not shown for symmetric fild or any fields on the user object.
        // - Default values are also not supported for users object as user record creation goes through a different path
        // - This config is not shown for synthetic fields (as users don't input values for them)
        return null
    }

    return (
        <>
            <Flex column wrap="nowrap" align="stretch">
                <Flex justify="space-between" mb={2} mt={3}>
                    <Text>Default</Text>
                    <Text variant="checkbox">
                        {/* eslint-disable-next-line react/jsx-no-target-blank */}
                        <a target="_blank" href={HELP_LINKS.DEFAULT_FIELD_VALUES}>
                            Learn more
                        </a>
                    </Text>
                </Flex>

                <FormField
                    variant="settings"
                    label="Default"
                    as={DefaultValueWrapper}
                    name="default_value"
                    controlled
                    value={field.default_value}
                    placeholder="Default value for this field"
                    required={false}
                    // these props are expected to get passed on to RecordLookup
                    field={field}
                />
            </Flex>

            {showWarning && (
                <Box mt={4}>
                    <Banner icon="info" variant="Information">
                        If the field contains multiple values only one will be used.
                    </Banner>
                </Box>
            )}
        </>
    )
}
